import React from "react";
import { motion } from "framer-motion";
import {
  Target,
  TrendingUp,
  CheckCircle,
  Shield,
  Users,
  Lightbulb,
  Award,
  ArrowRight,
  AlertCircle,
  BarChart3,
  Gauge,
  Zap,
  FileCheck,
  Bug,
} from "lucide-react";
import { Link } from "react-router-dom";
import Navbar from "../../components/Navbar";
import Footer from "../../components/Footer";

const GapAnalysis_Details = () => {
  return (
    <>
      <Navbar />

      {/* Hero */}
      <section className="relative bg-gradient-to-br from-[#b8860b] via-[#d4a017] to-[#e5b441] text-white py-20 px-4 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-amber-500 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-yellow-400 rounded-full blur-3xl" />
        </div>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="relative max-w-4xl mx-auto text-center"
        >
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-sm mb-6">
            <Target size={18} className="text-amber-200" />
            <span>Performance Assessment and Strategic Planning</span>
          </div>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-5">
            Gap Analysis
          </h1>
          <p className="text-gray-100 text-base md:text-lg max-w-2xl mx-auto">
            Assess the difference between current performance and desired goals,
            identify improvement opportunities, and develop strategic plans to
            achieve business objectives and compliance requirements.
          </p>
        </motion.div>
      </section>

      {/* What Is */}
      <section className="py-14 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold mb-6 text-gray-900">
              What Is Gap Analysis?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>
                Gap analysis is a strategic tool used by organizations to assess
                the difference between their current performance and desired
                goals or standards. By conducting gap analysis, businesses
                identify gaps in processes, systems, compliance, or performance
                and develop actionable plans to bridge those gaps.
              </p>
              <p>Gap analysis process involves:</p>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  "Assessing Current Performance",
                  "Defining Desired Outcomes",
                  "Identifying Performance Gaps",
                  "Analyzing Root Causes",
                  "Developing Action Plans",
                  "Implementing Improvements",
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <CheckCircle
                      size={20}
                      className="text-amber-700 mt-0.5 flex-shrink-0"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-14 px-4 bg-gradient-to-br from-gray-50 to-amber-50">
        <div className="max-w-6xl mx-auto">
          <motion.h2
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold text-center mb-12 text-gray-900"
          >
            Key Features of Gap Analysis
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: Target,
                title: "Performance Gap Identification",
                desc: "Identifies discrepancies between current and desired performance levels.",
              },
              {
                icon: CheckCircle,
                title: "Evaluation of Compliance",
                desc: "Assesses whether business is meeting industry standards and regulatory requirements.",
              },
              {
                icon: Zap,
                title: "Resource Optimization",
                desc: "Identifies areas where resources are underutilized or misallocated.",
              },
              {
                icon: Lightbulb,
                title: "Actionable Insights",
                desc: "Provides clear insights into changes needed and steps required.",
              },
              {
                icon: TrendingUp,
                title: "Continuous Improvement",
                desc: "Supports ongoing evaluation and drives continuous enhancements.",
              },
              {
                icon: BarChart3,
                title: "Data-Driven Decision Making",
                desc: "Provides data-driven insights for informed strategic decisions.",
              },
            ].map((feature, idx) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg hover:border-l-4 hover:border-amber-600 transition-all"
                >
                  <Icon size={32} className="text-amber-700 mb-3" />
                  <h3 className="text-lg font-bold text-gray-900 mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {feature.desc}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why It Matters */}
      <section className="py-14 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold mb-8 text-gray-900">
              Why Gap Analysis Matters for Your Business
            </h2>
            <div className="space-y-6">
              {[
                {
                  title: "Identify and Address Weaknesses",
                  desc: "Identifies weaknesses in processes and systems that could hinder performance or compliance.",
                },
                {
                  title: "Ensure Compliance",
                  desc: "Regular gap analysis ensures consistent compliance with regulatory and industry requirements.",
                },
                {
                  title: "Improve Operational Efficiency",
                  desc: "Identifies inefficiencies enabling streamlined operations and cost reduction.",
                },
                {
                  title: "Enhance Strategic Alignment",
                  desc: "Ensures business is on track to meet strategic goals and long-term vision.",
                },
                {
                  title: "Facilitate Decision Making",
                  desc: "Provides data necessary for management to focus efforts and allocate resources effectively.",
                },
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="flex gap-4 p-4 bg-amber-50 rounded-lg border-l-4 border-amber-600"
                >
                  <Star
                    size={24}
                    className="text-amber-700 flex-shrink-0 mt-1"
                  />
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">
                      {item.title}
                    </h3>
                    <p className="text-gray-700 text-sm">{item.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-14 px-4 bg-gradient-to-br from-amber-50 to-gray-50">
        <div className="max-w-6xl mx-auto">
          <motion.h2
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold text-center mb-12 text-gray-900"
          >
            Benefits of Gap Analysis
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: TrendingUp,
                title: "Improved Performance",
                desc: "Gap analysis leads to enhanced productivity and efficiency across operations.",
              },
              {
                icon: Shield,
                title: "Enhanced Compliance",
                desc: "Ensures compliance with standards avoiding penalties and reputational damage.",
              },
              {
                icon: Zap,
                title: "Optimized Resource Allocation",
                desc: "Identifies underused resources enabling more efficient allocation.",
              },
              {
                icon: Lightbulb,
                title: "Clear Path to Improvement",
                desc: "Provides clear roadmap for addressing weaknesses and achieving goals.",
              },
              {
                icon: BarChart3,
                title: "Informed Decision Making",
                desc: "Provides data-driven insights for strategic decisions and resource allocation.",
              },
              {
                icon: Award,
                title: "Strategic Alignment",
                desc: "Ensures operations align with business objectives and strategic vision.",
              },
            ].map((benefit, idx) => {
              const Icon = benefit.icon;
              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.08 }}
                  className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-all text-center"
                >
                  <Icon size={36} className="text-amber-700 mx-auto mb-3" />
                  <h3 className="font-bold text-gray-900 mb-2">
                    {benefit.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {benefit.desc}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How to Conduct */}
      <section className="py-14 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold mb-10 text-gray-900">
              How to Conduct a Gap Analysis
            </h2>
            <div className="space-y-6">
              {[
                {
                  step: "1",
                  title: "Define Scope and Objectives",
                  desc: "Identify areas requiring assessment and set clear objectives for the analysis.",
                },
                {
                  step: "2",
                  title: "Gather Data",
                  desc: "Collect data related to current performance, processes, and practices.",
                },
                {
                  step: "3",
                  title: "Compare with Desired State",
                  desc: "Determine desired future state and compare with current state to identify discrepancies.",
                },
                {
                  step: "4",
                  title: "Identify the Gaps",
                  desc: "Analyze differences to identify gaps in performance, compliance, or processes.",
                },
                {
                  step: "5",
                  title: "Develop and Implement Action Plan",
                  desc: "Create detailed plans outlining steps to close gaps with timelines and responsibilities.",
                },
                {
                  step: "6",
                  title: "Track Progress and Reassess",
                  desc: "Monitor implementation and reassess periodically ensuring sustained improvements.",
                },
              ].map((process, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="flex gap-6 p-6 bg-amber-50 rounded-lg"
                >
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-600 to-yellow-700 flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold">{process.step}</span>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-2">
                      {process.title}
                    </h3>
                    <p className="text-gray-700">{process.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Industries */}
      <section className="py-14 px-4 bg-gradient-to-br from-gray-50 to-amber-50">
        <div className="max-w-6xl mx-auto">
          <motion.h2
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold text-center mb-12 text-gray-900"
          >
            Industries Benefiting from Gap Analysis
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              "Technology and Software Development",
              "Manufacturing and Operations",
              "Healthcare and Pharmaceuticals",
              "Financial Services",
              "Quality and Process Management",
              "Strategic Planning and Consulting",
            ].map((industry, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.08 }}
                className="bg-white rounded-lg p-6 shadow-md border-l-4 border-amber-600 text-center"
              >
                <Target size={32} className="text-amber-700 mx-auto mb-3" />
                <h3 className="font-bold text-gray-900">{industry}</h3>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-gradient-to-br from-[#b8860b] via-[#d4a017] to-[#e5b441] rounded-2xl p-10 text-white text-center shadow-lg"
          >
            <Target size={40} className="mx-auto mb-4 text-yellow-200" />
            <h3 className="text-2xl font-bold mb-3">
              Why Choose MBA Solutions for Gap Analysis?
            </h3>
            <p className="text-yellow-100 mb-6 text-sm leading-relaxed">
              MBA Solutions provides expert gap analysis services, helping
              businesses assess current state, identify improvement
              opportunities, and develop strategic action plans for achieving
              business objectives.
            </p>
            <div className="bg-white/10 rounded-lg p-4 mb-6 text-left space-y-2 text-sm">
              <p className="flex items-center gap-2">
                <CheckCircle
                  size={18}
                  className="text-yellow-200 flex-shrink-0"
                />
                Gap Analysis for Compliance (ISO, regulations, risk management)
              </p>
              <p className="flex items-center gap-2">
                <CheckCircle
                  size={18}
                  className="text-yellow-200 flex-shrink-0"
                />
                Process Efficiency and Performance Assessment
              </p>
              <p className="flex items-center gap-2">
                <CheckCircle
                  size={18}
                  className="text-yellow-200 flex-shrink-0"
                />
                Action Plan Development and Implementation
              </p>
              <p className="flex items-center gap-2">
                <CheckCircle
                  size={18}
                  className="text-yellow-200 flex-shrink-0"
                />
                Continuous Monitoring and Reporting
              </p>
              <p className="flex items-center gap-2">
                <CheckCircle
                  size={18}
                  className="text-yellow-200 flex-shrink-0"
                />
                Ongoing Support for Continuous Improvement
              </p>
            </div>
            <div className="space-y-2 text-sm mb-6">
              <p>
                📧 Email:{" "}
                <a
                  href="mailto:atif@mbasolutions.co"
                  className="underline hover:text-yellow-100"
                >
                  atif@mbasolutions.co
                </a>
              </p>
              <p>
                🌐 Website:{" "}
                <a
                  href="https://www.mbasolutions.co"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-yellow-100"
                >
                  www.mbasolutions.co
                </a>
              </p>
            </div>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 px-8 py-3 rounded-full bg-white text-amber-800 font-bold hover:bg-yellow-50 transition-all"
            >
              Get Started <ArrowRight size={18} />
            </Link>
          </motion.div>
        </div>
      </section>

      <Footer />
    </>
  );
};

const Star = ({ size = 24, ...props }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    {...props}
  >
    <polygon points="12 2 15.09 9.26 23 9.26 17.19 14.31 19.29 21.74 12 16.69 4.71 21.74 6.81 14.31 1 9.27 8.91 9.26 12 2" />
  </svg>
);

export default GapAnalysis_Details;
